<template>
  <TContainer class="mt-8 md:mt-12 relative">
    <TOverlay :show="isCreating || isGettingPrices" class="z-20" />

    <div class="clear-both">
      <div class="float-left w-full lg:w-2/3 lg:pr-2">
        <div class="">
          <ApiResponseError
            v-if="hasError"
            :api-response-error="formError"
            class="mb-8"
          />

          <template v-if="!createSuccess">
            <div v-if="cottageContactPage">
              <TTitle tag="h1" size="2xl" classes="mb-2">
                {{ t('views.cottage-contact.title') }}
              </TTitle>
              <TText
                v-if="
                  cottageContactPage.owner.answer &&
                  cottageContactPage.owner.answer.rate !== null
                "
                classes="mb-2"
              >
                {{
                  cottageContactPage.owner.answer.rate > 0
                    ? t('views.cottage-contact.answerRate', {
                        rate: cottageContactPage.owner.answer.rate,
                        range: t(
                          `range.${cottageContactPage.owner.answer.range}`,
                        ),
                        value: cottageContactPage.owner.answer.value,
                        unit: t(
                          `time.${cottageContactPage.owner.answer.unit}`,
                          cottageContactPage.owner.answer.value,
                        ),
                      })
                    : t('views.cottage-contact.noAnswerRate')
                }}
              </TText>
              <UButton
                color="primary"
                variant="link"
                :label="t('views.cottage-contact.cottageLink')"
                :to="
                  routes().cottageDetail({
                    slug: cottageContactPage.slug,
                    province: cottageContactPage.province.slug,
                  })
                "
              ></UButton>
              <TSeparator class="mt-6 mb-8" />
            </div>
          </template>
        </div>
      </div>
      <div class="float-right w-full lg:w-1/3 lg:pl-2">
        <div class="">
          <template v-if="!createSuccess">
            <div v-if="cottageContactPage">
              <UCard :ui="publicCard">
                <TText variant="semibold" classes="mb-2">
                  {{ t('views.cottage-contact.price.message') }}
                </TText>
                <CottageContactPrice
                  :item="cottageContactPage"
                  @submit="onPriceSubmit"
                  @update-dates="onPriceUpdateDates"
                  @update-people="onPriceUpdatePeople"
                />

                <CottageContactPriceResponse
                  v-if="pricesResponse"
                  :item="pricesResponse"
                />
              </UCard>
            </div>
          </template>
        </div>
      </div>
      <div class="float-left w-full lg:w-2/3 lg:pr-2 mb-20">
        <div class="">
          <template v-if="!createSuccess">
            <div v-if="cottageContactPage">
              <CottageContactForm
                :item="cottageContactPage"
                @submit="onSubmit"
              />
              <TText size="sm" color="text-gray-600" classes="mt-4">
                {{ t('views.cottage-contact.underFormMessage') }}
              </TText>
            </div>
          </template>

          <CottageContactPageSuccess
            v-if="createSuccess && cottageContactPage"
            :id="cottageContactPage.id"
          />
        </div>
      </div>
    </div>
  </TContainer>
</template>

<script setup lang="ts">
// Composables
import { useCottageContactPageEmail } from '../composables/useCottageContactPageEmail'
import { useCottageContactPagePrices } from '../composables/useCottageContactPagePrices'
import {
  sendGa4ContactEvent,
  sendCottageConversionAll,
} from '~~/modules/integrations/ga4/composables/useAnalyticsGa4Composable'
import { sendAdsContact } from '~~/modules/integrations/ads/composables/useAnalyticsAdsComposable'
import {
  sendConnectifPurchase,
  sendConnectifPageProductCart,
} from '~~/modules/integrations/connectif/composables/useConnectifComposable'

// Entities
import type { CottageContactPage } from '../entities/CottageContactPage'
import type {
  CottageContactPageCreateForm,
  CottageContactPageCreateResponse,
} from '../entities/CottageContactPageCreate'
import type {
  CottageContactPagePricesForm,
  CottageContactPagePricesDates,
  CottageContactPagePricesPeople,
  CottageContactPagePricesResponse,
} from '../entities/CottageContactPagePrices'

// Constants
import { CONNECTIF_DEFAULT_PRICE } from '~~/modules/integrations/connectif/constants/index'

// Design System
import { publicCard } from '~~/modules/design-system/utils/nuxt-ui-config/components/card'

// Composables
const { sendFormEmailData } = useCottageContactPageEmail()
const { getPricesData } = useCottageContactPagePrices()
const { setCookieRuralityCottageContact } = useCookiesRurality()

// Component state
const isCreating = ref(false)
const isGettingPrices = ref(false)
const hasError = ref(false)
const createSuccess = ref(false)

// Props
interface propsInterface {
  cottageContactPage: CottageContactPage
}
const props = defineProps<propsInterface>()

// Cart ID ref
const ConnectifCartId: Ref<string | null> = ref(null)
const ConnectifTotalPrice: Ref<number> = ref(CONNECTIF_DEFAULT_PRICE.CONTACT)

// Constants
let formError: ApiResponseError | null = null
const dates = ref<CottageContactPagePricesDates | null>(null)
const people = ref<CottageContactPagePricesPeople | null>(null)

const pricesResponse = ref<CottageContactPagePricesResponse | null>(null)

// Methods
const onPriceSubmit = async (ev: CottageContactPagePricesForm) => {
  isGettingPrices.value = true
  if (props.cottageContactPage) {
    const responseGetPricesData = await getPricesData({
      cottageContactPage: props.cottageContactPage,
      dates: ev.dates,
      people: ev.people,
    })
    pricesResponse.value = responseGetPricesData

    if (pricesResponse.value && pricesResponse.value.value) {
      ConnectifTotalPrice.value = pricesResponse.value.value
    }
  }
  isGettingPrices.value = false
}

const onPriceUpdateDates = async (ev: CottageContactPagePricesDates) => {
  dates.value = ev
}

const onPriceUpdatePeople = async (ev: CottageContactPagePricesPeople) => {
  people.value = ev
}

const onSubmit = async (ev: CottageContactPageCreateForm) => {
  isCreating.value = true
  hasError.value = false
  if (props.cottageContactPage) {
    const responseSendFormData = await sendFormEmailData({
      createForm: ev,
      cottageContactPage: props.cottageContactPage,
      dates: dates.value,
      people: people.value,
    })
    if (responseSendFormData.apiResponseError) {
      formError = responseSendFormData.apiResponseError
      hasError.value = true
      isCreating.value = false
    } else if (responseSendFormData.apiResponseSuccess) {
      onSuccess(responseSendFormData.apiResponseSuccess, ev)
    }
  }
}

const onSuccess = (
  cottageContactPageCreateResponse: CottageContactPageCreateResponse,
  formData: CottageContactPageCreateForm,
) => {
  if (props.cottageContactPage) {
    setDataRepository(formData)

    sendGa4ContactEvent({
      province: props.cottageContactPage.province.name,
      method: 'email',
    })
    sendCottageConversionAll()
    if (cottageContactPageCreateResponse) {
      sendAdsContact({
        email: cottageContactPageCreateResponse.traveler.email,
        phone: cottageContactPageCreateResponse.traveler.phone,
      })
    }

    // Connectif
    if (
      ConnectifCartId.value &&
      cottageContactPageCreateResponse.traveler.email
    ) {
      sendConnectifPurchase(
        {
          ...props.cottageContactPage,
          cartId: ConnectifCartId.value,
          productPrice: ConnectifTotalPrice.value,
          paymentMethod: 'Contact',
        },
        {
          entityInfo: {
            primaryKey: cottageContactPageCreateResponse.traveler.email,
          },
        },
      )
    }

    isCreating.value = false
    createSuccess.value = true
  }
}

const setDataRepository = (formData: CottageContactPageCreateForm) => {
  setCookieRuralityCottageContact({
    message: formData.message,
    traveler_email: formData.email,
    traveler_name: formData.name,
    traveler_phone: formData.phone,
  })
}

// Initialize cart and send tracking events
onMounted(() => {
  if (props.cottageContactPage) {
    ConnectifCartId.value = new Date().getTime().toString()

    sendConnectifPageProductCart({
      ...props.cottageContactPage,
      cartId: ConnectifCartId.value,
      productPrice: ConnectifTotalPrice.value,
      paymentMethod: 'Contact',
    })
  }
})
</script>
